import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Paper, Typography, Button, IconButton, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Grid, Pagination, Chip, MenuItem,
  Checkbox, FormControlLabel, Badge
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { useAuth } from "../../context/AuthContext";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4000";
const PAGE_SIZE = 10;
const PRIORITIES = ["low", "medium", "high", "critical"];

function toCSV(rows) {
  if (!rows?.length) return "";
  const headers = [
    "opened_at_utc", "title", "description", "priority", "assigned_to_name",
    "status", "resolution_note", "resolved_at_utc",
  ];
  const lines = [headers.join(",")];
  for (const r of rows) {
    const line = [
      r.opened_at_utc || "",
      r.title || "",
      r.description || "",
      r.priority || "medium",
      r.assigned_to_name || "",
      r.status || "",
      r.resolution_note || "",
      r.resolved_at_utc || "",
    ]
      .map(v => String(v).replace(/"/g, '""'))
      .map(v => /[",\n]/.test(v) ? `"${v}"` : v)
      .join(",");
    lines.push(line);
  }
  return lines.join("\n");
}

// Same visual language as JobDashboard.jsx's PriorityChip/IssueAgeChip —
// duplicated locally since this component doesn't share that file.
function PriorityChip({ value }) {
  const v = String(value || "medium").toLowerCase();
  const map = {
    critical: { label: "Critical", color: "error" },
    high: { label: "High", color: "warning" },
    medium: { label: "Medium", color: "info" },
    low: { label: "Low", color: "default" },
  };
  const conf = map[v] || map.medium;
  return <Chip size="small" label={conf.label} color={conf.color} sx={{ fontWeight: 700 }} />;
}

function IssueAgeChip({ openedAtUtc, daysOpen }) {
  let days = daysOpen;
  if (days == null && openedAtUtc) {
    const opened = new Date(`${openedAtUtc.replace(" ", "T")}Z`);
    if (!isNaN(opened.getTime())) {
      days = Math.floor((Date.now() - opened.getTime()) / 86400000);
    }
  }
  if (days == null) return <Chip size="small" variant="outlined" label="—" />;
  let color = "success";
  let label = days === 0 ? "Today" : `${days}d`;
  if (days >= 2 && days < 5) color = "warning";
  if (days >= 5) color = "error";
  return <Chip size="small" color={color} label={label} />;
}

export default function IssueTable({ jobId }) {
  const { user } = useAuth();
  const role = (user?.role || "engineer" || "storekeeper").toLowerCase();
  const isAdmin = role === "admin";
  const canResolve = role === "admin" || role === "engineer" || role === "storekeeper";
  const token = localStorage.getItem("token");

  // Same behavior as apiClient.js's response interceptor — but this file
  // uses plain fetch(), which that interceptor can't reach. A 401 here
  // (expired/invalid token) should be just as visible: clear the stale
  // session and send the person back to login, rather than surfacing a
  // raw "token missing" error with no explanation.
  const handleAuthFailure = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    window.location.href = "/login?reason=session_expired";
  };

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  // Add dialog
  const [openAdd, setOpenAdd] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("medium");
  const [selectedAssignees, setSelectedAssignees] = useState([]); // multiple assignees now
  const [addAssigneeSearch, setAddAssigneeSearch] = useState("");

  // Assignable users — real link (id + name), not free text
  const [assignableUsers, setAssignableUsers] = useState([]);

  // Resolve dialog
  const [openResolve, setOpenResolve] = useState(false);
  const [resolving, setResolving] = useState(null);
  const [resolutionNote, setResolutionNote] = useState("");

  // Edit (priority/assignment) dialog — feature 2: assign an issue to
  // one or more people without needing to resolve it.
  const [openEdit, setOpenEdit] = useState(false);
  const [editingIssue, setEditingIssue] = useState(null);
  const [editPriority, setEditPriority] = useState("medium");
  const [editSelectedAssignees, setEditSelectedAssignees] = useState([]);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  // Remark: admin-only override — lets an admin add/correct a note on
  // ANY issue at any time, distinct from the engineer's own
  // Resolve-time resolution note.
  const [editRemark, setEditRemark] = useState("");
  const [editAssigneeSearch, setEditAssigneeSearch] = useState("");

  // Delete dialog
  const [openDelete, setOpenDelete] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const pending = useRef(false);

  const fetchIssues = async (opts = {}) => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      qs.set("page", opts.page ?? page);
      qs.set("limit", PAGE_SIZE);
      const res = await fetch(`${API_BASE}/api/jobs/${jobId}/issues?${qs.toString()}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to fetch issues");
      setRows(data.records || []);
      setTotal(Number(data.total || 0));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchIssues({ page: 1 }); /* eslint-disable-next-line */ }, [jobId]);
  useEffect(() => { fetchIssues({ page }); /* eslint-disable-next-line */ }, [page]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    fetch(`${API_BASE}/api/jobs/issues/assignable-users`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => setAssignableUsers(data?.users || []))
      .catch((e) => console.error("Failed to load assignable users:", e));
  }, []);

  const openAddDialog = () => {
    setOpenAdd(true);
    setTitle("");
    setDescription("");
    setPriority("medium");
    setSelectedAssignees([]);
    setAddAssigneeSearch("");
  };

  const toggleAddAssignee = (u) => {
    setSelectedAssignees((prev) =>
      prev.some((x) => x.id === u.id) ? prev.filter((x) => x.id !== u.id) : [...prev, u]
    );
  };

  const submitAdd = async () => {
    if (pending.current) return;
    if (!title || !description) return alert("Title and Description are required.");
    pending.current = true;
    try {
      const res = await fetch(`${API_BASE}/api/jobs/${jobId}/issues`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          title,
          description,
          priority,
          assigned_to_ids: selectedAssignees.map((u) => u.id),
        }),
      });
      const data = await res.json();
      if (res.status === 401) return handleAuthFailure();
      if (!data.success) throw new Error(data.error || "Create failed");
      setOpenAdd(false);
      await fetchIssues();
    } catch (e) {
      console.error(e);
      alert(e.message);
    } finally {
      pending.current = false;
    }
  };

  // Feature 2: assign/reprioritize an issue independently of resolving it.
  const openEditDialog = (rec) => {
    setEditingIssue(rec);
    setEditPriority(rec.priority || "medium");
    setEditTitle(rec.title || "");
    setEditDescription(rec.description || "");
    setEditRemark(rec.resolution_note || "");
    // rec.assignees is the real multi-assignee list from the backend.
    // Fall back to the legacy single assigned_to/assigned_to_name if
    // assignees isn't populated yet (e.g. migration just ran, no data).
    setEditSelectedAssignees(
      Array.isArray(rec.assignees) && rec.assignees.length
        ? rec.assignees
        : rec.assigned_to_name
        ? [{ id: rec.assigned_to, name: rec.assigned_to_name, role: "" }]
        : []
    );
    setEditAssigneeSearch("");
    setOpenEdit(true);
  };

  const toggleEditAssignee = (u) => {
    setEditSelectedAssignees((prev) =>
      prev.some((x) => x.id === u.id) ? prev.filter((x) => x.id !== u.id) : [...prev, u]
    );
  };

  const submitEdit = async () => {
    if (pending.current || !editingIssue) return;
    pending.current = true;
    try {
      const res = await fetch(`${API_BASE}/api/jobs/${jobId}/issues/${editingIssue.issue_id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          title: editTitle,
          description: editDescription,
          priority: editPriority,
          assigned_to_ids: editSelectedAssignees.map((u) => u.id),
          // Only admins can override the remark/resolution note outside
          // the normal Resolve flow — everyone else's edit leaves it as-is.
          ...(isAdmin ? { resolution_note: editRemark } : {}),
        }),
      });
      const data = await res.json();
      if (res.status === 401) return handleAuthFailure();
      if (!data.success) throw new Error(data.error || "Update failed");
      setOpenEdit(false);
      await fetchIssues();
    } catch (e) {
      console.error(e);
      alert(e.message);
    } finally {
      pending.current = false;
    }
  };

  const openResolveDialog = (rec) => {
    setResolving(rec);
    setResolutionNote(rec.resolution_note || "");
    setOpenResolve(true);
  };
  const submitResolve = async () => {
    if (pending.current) return;
    if (!canResolve) return;
    pending.current = true;
    try {
    const res = await fetch(`${API_BASE}/api/jobs/${jobId}/issues/${resolving.issue_id}`, {
  method: "PATCH",
  headers: { 
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  },
  body: JSON.stringify({ status: "resolved", resolution_note: resolutionNote }),
});

      const data = await res.json();
      if (res.status === 401) return handleAuthFailure();
      if (!data.success) throw new Error(data.error || "Resolve failed");
      setOpenResolve(false);
      await fetchIssues();
    } catch (e) {
      console.error(e);
      alert(e.message);
    } finally {
      pending.current = false;
    }
  };

  const confirmDelete = (rec) => { setDeleting(rec); setOpenDelete(true); };
  const doDelete = async () => {
    if (pending.current) return;
    pending.current = true;
    try {
      const res = await fetch(`${API_BASE}/api/jobs/${jobId}/issues/${deleting.issue_id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.status === 401) return handleAuthFailure();
      if (!data.success) throw new Error(data.error || "Delete failed");
      setOpenDelete(false);
      if (rows.length - 1 <= 0 && page > 1) {
        setPage(page - 1);
        await fetchIssues({ page: page - 1 });
      } else {
        await fetchIssues();
      }
    } catch (e) {
      console.error(e);
      alert(e.message);
    } finally {
      pending.current = false;
    }
  };

  const exportCSV = async () => {
    const qs = new URLSearchParams({ page: 1, limit: 10000 });
    const res = await fetch(`${API_BASE}/api/jobs/${jobId}/issues?${qs.toString()}`);
    const data = await res.json();
    if (!data.success) return;
    const csv = toCSV(data.records || []);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `job_${jobId}_issues.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportPDF = async () => {
    try {
      const { default: jsPDF } = await import("jspdf");
      await import("jspdf-autotable");
      const qs = new URLSearchParams({ page: 1, limit: 10000 });
      const res = await fetch(`${API_BASE}/api/jobs/${jobId}/issues?${qs.toString()}`);
      const data = await res.json();
      if (!data.success) return;

      const doc = new jsPDF({ orientation: "landscape" });
      doc.setFontSize(14);
      doc.text(`Job ${jobId} — Issues (Filtered)`, 14, 14);

      const head = [["Opened At (UTC)", "Issue Title", "Description of the Issue", "Priority", "Assigned To", "Status", "Resolution Note", "Resolved At (UTC)"]];
      const body = (data.records || []).map(r => [
        r.opened_at_utc || "",
        r.title || "",
        r.description || "",
        r.priority || "medium",
        r.assigned_to_name || "Unassigned",
        r.status || "",
        r.resolution_note || "",
        r.resolved_at_utc || "",
      ]);

      doc.autoTable({ startY: 20, head, body, styles: { fontSize: 9 }, headStyles: { fillColor: [33,33,33] } });
      doc.save(`job_${jobId}_issues.pdf`);
    } catch (e) {
      alert("To export PDF, install: npm i jspdf jspdf-autotable");
      console.error("Export PDF error:", e);
    }
  };

  const StatusChip = useMemo(() => ({ value }) => {
    const v = (value || "").toLowerCase();
    const color = v === "resolved" ? "#2e7d32" : "#1e88e5";
    return <Chip label={(v || "open").toUpperCase()} size="small" sx={{ bgcolor: color, color: "#fff", fontWeight: 700 }} />;
  }, []);

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="h6" fontWeight={700}>Issue Records</Typography>
        <Box>
         
          <Button variant="contained" startIcon={<AddCircleIcon />} onClick={openAddDialog}>Add Issue</Button>
        </Box>
      </Box>

      {/* Table */}
      <Box component="table" sx={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
        <thead>
          <tr>
            <th style={th}>Opened At (UTC)</th>
            <th style={th}>Issue Title</th>
            <th style={th}>Description of the Issue</th>
            <th style={th}>Priority</th>
            <th style={th}>Age</th>
            <th style={th}>Assigned To</th>
            <th style={th}>Status</th>
            <th style={th}>Resolution Note</th>
            <th style={{ ...th, textAlign: "center", width: 190 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={9} style={td}>Loading…</td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={9} style={td}>No issues.</td></tr>
          ) : (
            rows.map(r => (
              <tr key={r.issue_id}>
                <td style={td}>{r.opened_at_utc || "—"}</td>
                <td style={td}>{r.title || "—"}</td>
                <td style={td}>{r.description || "—"}</td>
                <td style={td}><PriorityChip value={r.priority} /></td>
                <td style={td}><IssueAgeChip openedAtUtc={r.opened_at_utc} daysOpen={r.days_open} /></td>
                <td style={td}>{r.assigned_to_name || <span style={{ opacity: 0.5 }}>Unassigned</span>}</td>
                <td style={td}><StatusChip value={r.status} /></td>
                <td style={td}>{r.resolution_note || "—"}</td>
                <td style={{ ...td, textAlign: "center" }}>
                  {/* Assign / reprioritize (admin + engineer) */}
                  <Tooltip
                    title={
                      r.status === "resolved" && !isAdmin
                        ? "Resolved — reopen the issue before changing priority or assignment"
                        : "Edit Issue"
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => openEditDialog(r)}
                        disabled={!canResolve || (r.status === "resolved" && !isAdmin)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {/* Resolve (admin + engineer) */}
                  <Tooltip title="Resolve">
                    <span>
                      <IconButton size="small" onClick={() => openResolveDialog(r)} disabled={!canResolve}>
                        <CheckCircleIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {/* Delete (admin only) */}
                  <Tooltip title={isAdmin ? "Delete" : "Admin only"}>
                    <span>
                      <IconButton size="small" onClick={() => { setDeleting(r); setOpenDelete(true); }} disabled={!isAdmin} sx={{ color: isAdmin ? "error.main" : "action.disabled" }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Box>

      {/* Pagination */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}>
        <Pagination
          count={Math.max(1, Math.ceil(total / PAGE_SIZE))}
          page={page}
          onChange={(_, v) => setPage(v)}
          color="primary"
          variant="outlined"
          shape="rounded"
        />
      </Box>

      {/* Add Issue */}
      <Dialog open={openAdd} onClose={() => setOpenAdd(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add Issue</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <TextField label="Issue Title (err01, err09)" fullWidth value={title} onChange={(e) => setTitle(e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Descriptions of Issue" fullWidth multiline minRows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label="Priority" fullWidth value={priority} onChange={(e) => setPriority(e.target.value)}>
                {PRIORITIES.map((p) => (
                  <MenuItem key={p} value={p} sx={{ textTransform: "capitalize" }}>{p}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                Assign To (optional, multiple allowed)
              </Typography>
              <TextField
                size="small"
                fullWidth
                placeholder="Filter the list..."
                value={addAssigneeSearch}
                onChange={(e) => setAddAssigneeSearch(e.target.value)}
                sx={{ mb: 1 }}
              />
              <Box sx={{ maxHeight: 220, overflowY: "auto", border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1 }}>
                {assignableUsers
                  .filter((u) =>
                    !addAssigneeSearch.trim()
                      ? true
                      : `${u.name} ${u.role}`.toLowerCase().includes(addAssigneeSearch.trim().toLowerCase())
                  )
                  .map((u) => {
                    const checked = selectedAssignees.some((sel) => sel.id === u.id);
                    return (
                      <Box key={u.id} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1 }}>
                        <FormControlLabel
                          control={<Checkbox checked={checked} onChange={() => toggleAddAssignee(u)} />}
                          label={`${u.name} (${u.role})`}
                        />
                        <Box sx={{ display: "flex", gap: 1 }}>
                          {u.job_count > 0 && (
                            <Tooltip title={`Already assigned to ${u.job_count} job(s)`}>
                              <Badge badgeContent={u.job_count} color="primary">
                                <Chip size="small" label="jobs" variant="outlined" />
                              </Badge>
                            </Tooltip>
                          )}
                          {u.open_issue_count > 0 && (
                            <Tooltip title={`Already assigned to ${u.open_issue_count} open issue(s)`}>
                              <Badge badgeContent={u.open_issue_count} color="error">
                                <Chip size="small" label="issues" variant="outlined" />
                              </Badge>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                {assignableUsers.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                    No users found.
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAdd(false)}>Cancel</Button>
          <Button onClick={submitAdd} variant="contained">Add Issue</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Issue (title/description/priority/assignment/remark) */}
      <Dialog open={openEdit} onClose={() => setOpenEdit(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Issue</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Title"
                fullWidth
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                fullWidth
                multiline
                minRows={2}
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField select label="Priority" fullWidth value={editPriority} onChange={(e) => setEditPriority(e.target.value)}>
                {PRIORITIES.map((p) => (
                  <MenuItem key={p} value={p} sx={{ textTransform: "capitalize" }}>{p}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                Assigned To (multiple allowed)
              </Typography>
              <TextField
                size="small"
                fullWidth
                placeholder="Filter the list..."
                value={editAssigneeSearch}
                onChange={(e) => setEditAssigneeSearch(e.target.value)}
                sx={{ mb: 1 }}
              />
              <Box sx={{ maxHeight: 260, overflowY: "auto", border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1 }}>
                {assignableUsers
                  .filter((u) =>
                    !editAssigneeSearch.trim()
                      ? true
                      : `${u.name} ${u.role}`.toLowerCase().includes(editAssigneeSearch.trim().toLowerCase())
                  )
                  .map((u) => {
                    const checked = editSelectedAssignees.some((sel) => sel.id === u.id);
                    return (
                      <Box key={u.id} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1 }}>
                        <FormControlLabel
                          control={<Checkbox checked={checked} onChange={() => toggleEditAssignee(u)} />}
                          label={`${u.name}${u.role ? ` (${u.role})` : ""}`}
                        />
                        <Box sx={{ display: "flex", gap: 1 }}>
                          {u.job_count > 0 && (
                            <Tooltip title={`Already assigned to ${u.job_count} job(s)`}>
                              <Badge badgeContent={u.job_count} color="primary">
                                <Chip size="small" label="jobs" variant="outlined" />
                              </Badge>
                            </Tooltip>
                          )}
                          {u.open_issue_count > 0 && (
                            <Tooltip title={`Already assigned to ${u.open_issue_count} open issue(s)`}>
                              <Badge badgeContent={u.open_issue_count} color="error">
                                <Chip size="small" label="issues" variant="outlined" />
                              </Badge>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                {assignableUsers.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                    No users found.
                  </Typography>
                )}
              </Box>
            </Grid>
            {isAdmin && (
              <Grid item xs={12}>
                <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                  Admin Remark
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                  Add or correct a note on this issue at any time — separate from the resolution note
                  an engineer leaves when actually resolving it.
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  placeholder="Admin note..."
                  value={editRemark}
                  onChange={(e) => setEditRemark(e.target.value)}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
          <Button onClick={submitEdit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Resolve Issue */}
      <Dialog open={openResolve} onClose={() => setOpenResolve(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Resolve Issue</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ mb: 1, fontWeight: 600 }}>{resolving?.title}</Typography>
          <TextField
            label="Resolution Note"
            fullWidth
            multiline
            minRows={2}
            value={resolutionNote}
            onChange={(e) => setResolutionNote(e.target.value)}
            placeholder="Explain how you resolved this issue"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenResolve(false)}>Cancel</Button>
          <Button onClick={submitResolve} variant="contained" startIcon={<CheckCircleIcon />}>Mark Resolved</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Issue */}
      <Dialog open={openDelete} onClose={() => setOpenDelete(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: "error.main" }}>Delete Issue?</DialogTitle>
        <DialogContent dividers>
          This will permanently remove the issue. Admin only.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDelete(false)}>Cancel</Button>
          <Button onClick={doDelete} color="error" variant="contained" startIcon={<DeleteIcon />}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

const th = {
  padding: "10px 12px",
  fontSize: 12,
  fontWeight: 700,
  borderBottom: "1px solid #eee",
  textAlign: "left",
  color: "#333",
};
const td = {
  padding: "10px 12px",
  fontSize: 14,
  borderBottom: "1px solid #f1f1f1",
};
